import type { InputBaseModel } from '../models';
import { Utils } from '../../custom_shared/misc';

export const AdditionalClassificationSourceIdPrefix = 'additionalClassification_';

type ClassificationInput = Pick<InputBaseModel, 'id' | 'guid' | 'parentInputIds' | 'sourceId'>;

export default class ClassificationInputLayoutUtils {
    static isAdditionalClassificationInput(input: ClassificationInput) {
        return !!input.sourceId?.startsWith(AdditionalClassificationSourceIdPrefix);
    }

    static getAdditionalClassificationInputs<T extends ClassificationInput>(inputs: T[]) {
        return inputs.filter(ClassificationInputLayoutUtils.isAdditionalClassificationInput);
    }

    static resolveStandardParentInputIdByAdditionalInputGuid<T extends ClassificationInput>(
        additionalClassificationInputs: T[],
        renderedStandardInputIds: string[]
    ) {
        const inputsById = new Map(additionalClassificationInputs.map(input => [input.id, input]));
        const parentInputIdByGuid = new Map<string, string>();
        const resolvingInputGuids = new Set<string>();

        const resolveStandardParentInputId = (input: T): string | undefined => {
            const existingParentInputId = parentInputIdByGuid.get(input.guid);

            if (existingParentInputId) {
                return existingParentInputId;
            }

            if (resolvingInputGuids.has(input.guid)) {
                return undefined;
            }

            resolvingInputGuids.add(input.guid);

            const parentInputIds = input.parentInputIds ?? [];
            const additionalClassificationParentInputs = parentInputIds
                .map(parentInputId => inputsById.get(parentInputId))
                .filter((parentInput): parentInput is T => !!parentInput);
            const resolvedParentInputId = additionalClassificationParentInputs.length
                ? additionalClassificationParentInputs.map(resolveStandardParentInputId).find(Boolean)
                : parentInputIds.find(parentInputId => renderedStandardInputIds.includes(parentInputId));

            resolvingInputGuids.delete(input.guid);

            if (resolvedParentInputId) {
                parentInputIdByGuid.set(input.guid, resolvedParentInputId);
            }

            return resolvedParentInputId;
        };

        additionalClassificationInputs.forEach(resolveStandardParentInputId);

        return parentInputIdByGuid;
    }

    static getAdditionalClassificationInputsForStandardParent<T extends ClassificationInput>(
        additionalClassificationInputs: T[],
        parentInputIdByGuid: Map<string, string>,
        standardParentInputId: string
    ) {
        return additionalClassificationInputs.filter(
            input => parentInputIdByGuid.get(input.guid) === standardParentInputId
        );
    }

    static getStandaloneAdditionalClassificationInputs<T extends ClassificationInput>(
        additionalClassificationInputs: T[],
        parentInputIdByGuid: Map<string, string>
    ) {
        return additionalClassificationInputs.filter(input => !parentInputIdByGuid.has(input.guid));
    }

    static groupAdditionalClassificationInputs<T extends ClassificationInput>(inputs: T[]) {
        return Utils.groupArray(inputs, 3);
    }
}
